import { createStore } from '@shared/utils/state/state-factory'
import { omit } from 'lodash'
import { getLocalAuth } from 'v2/utils'

export interface AppState {
  isDarkMode: boolean
  agentStatus: AgentStatusUpdate
  isHydrated: boolean
  userAuthenticated: boolean
  isOnline: boolean
  appFocused: boolean
  showDuplicateSidebar: boolean
  switchingToOrgName: string | null
  isAssistantOpen: boolean
  pendingAssistantPrompt: string | null
}

interface AgentStatusUpdate {
  uuid?: string
  updateType: 'initial' | 'manual' | 'idle'
}

interface Actions {
  setDarkMode: (mode: boolean) => void
  setAgentStatus: (status: AgentStatusUpdate) => void
  setIsHydrated: (isHydrated: boolean) => void
  reset: () => void
  setUserAuthenticated: (userAuthenticated: boolean) => void
  setIsOnline: (isOnline: boolean) => void
  setAppFocused: (appFocused: boolean) => void
  setShowDuplicateSidebar: (show: boolean) => void
  setSwitchingToOrgName: (orgName: string | null) => void
  setIsAssistantOpen: (open: boolean) => void
  setPendingAssistantPrompt: (prompt: string | null) => void
}

// State that is shared across the app
const initialState: AppState = {
  isDarkMode: false,
  isHydrated: false,
  userAuthenticated: !!getLocalAuth().auth_token,
  agentStatus: {
    uuid: '',
    updateType: 'initial', // Setting it initial so that the login event is triggered on login
  },
  isOnline: true,
  appFocused: true,
  showDuplicateSidebar: false,
  switchingToOrgName: null,
  isAssistantOpen: false,
  pendingAssistantPrompt: null,
}

export const useAppStore = createStore<AppState & Actions>(
  'app',
  (set) => ({
    ...initialState,
    // Preserve switchingToOrgName during reset so the org-switch loader stays visible through the logout/login flow
    reset: () => set(omit(initialState, 'isHydrated', 'userAuthenticated', 'switchingToOrgName')),
    setDarkMode: (mode) =>
      set((state) => {
        state.isDarkMode = mode
      }),
    setAgentStatus: (status) =>
      set((state) => {
        state.agentStatus = {
          ...state.agentStatus,
          ...status,
        }
      }),
    setIsHydrated: (isHydrated) =>
      set((state) => {
        state.isHydrated = isHydrated
      }),
    setUserAuthenticated: (userAuthenticated) =>
      set((state) => {
        state.userAuthenticated = userAuthenticated
      }),
    setIsOnline: (isOnline) =>
      set((state) => {
        state.isOnline = isOnline
      }),
    setAppFocused: (appFocused) =>
      set((state) => {
        state.appFocused = appFocused
      }),
    setShowDuplicateSidebar: (show) =>
      set((state) => {
        state.showDuplicateSidebar = show
      }),
    setSwitchingToOrgName: (orgName) =>
      set((state) => {
        state.switchingToOrgName = orgName
      }),
    setIsAssistantOpen: (open) =>
      set((state) => {
        state.isAssistantOpen = open
      }),
    setPendingAssistantPrompt: (prompt) =>
      set((state) => {
        state.pendingAssistantPrompt = prompt
      }),
  }),
  {
    shared: true,
    persist: true,
    onHydrate: () => () => {
      useAppStore.setState({
        isHydrated: true,
        userAuthenticated: !!getLocalAuth().auth_token,
        isOnline: true,
        // Clear org-switch loader after page reload completes
        switchingToOrgName: null,
        // Queued prompt is one-shot UI intent; never resurrect on reload.
        pendingAssistantPrompt: null,
      })
    },
  },
)
